/**
 * On a consortium's page, for someone invited to it: who invited them, and Accept / Decline right there
 * (the invitation email and the notification lead here too). Anyone invited may accept, verified or not.
 * Backend: GET /interact/consortium-invitations/pending-for-me/, POST …/<id>/accept/ | …/<id>/reject/.
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { GroupAdd as InviteIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';

export default function ConsortiumInvitationBanner({ consortiumId, consortiumName, onAnswered }) {
  const { showSnackbar } = useSnackbar();
  const { data, mutate } = useSWR('/interact/consortium-invitations/pending-for-me/', fetcher);
  const [busy, setBusy] = useState(null);       // 'accept' | 'reject'
  const invite = (Array.isArray(data) ? data : data?.results || [])
    .find((i) => String(i.consortium?.id) === String(consortiumId));
  if (!invite) return null;

  const from = invite.invitation?.from_user;
  const inviter = from?.name || [from?.first_name, from?.last_name].filter(Boolean).join(' ') || 'The coordinator';
  const answer = async (kind) => {
    setBusy(kind);
    try {
      await axiosService.post(`/interact/consortium-invitations/${invite.id}/${kind}/`);
      showSnackbar(kind === 'accept' ? `Welcome to ${consortiumName || 'the consortium'}` : 'Invitation declined', 'success');
      await mutate();
      onAnswered?.(kind);
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'The invitation could not be answered.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ bgcolor: '#1f2937', color: '#fff', px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <InviteIcon sx={{ opacity: 0.85 }} />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }}>
        <b>{inviter}</b> invited you to join <b>{consortiumName || 'this consortium'}</b>.
        {invite.invitation?.message ? <> “{invite.invitation.message}”</> : null}
      </Typography>
      <Button size="small" variant="contained" color="success" disabled={Boolean(busy)} onClick={() => answer('accept')}>
        {busy === 'accept' ? <CircularProgress size={16} color="inherit" /> : 'Accept'}
      </Button>
      <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => answer('reject')}
        sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
        {busy === 'reject' ? <CircularProgress size={16} color="inherit" /> : 'Decline'}
      </Button>
    </Box>
  );
}
